import type { EventProperties } from 'ProdantixSdk/core/types';

export const COLLECTED_FORM_EVENT = '$form_submitted';

const SKIPPED_TYPES = new Set(['button', 'file', 'hidden', 'image', 'password', 'reset', 'submit']);

const CARD_HINTS = /(card|cc-?num|cvc|cvv|iban|security.?code)/i;

type Capturer = { capture(eventName: string, options?: { properties?: EventProperties }): void };

const isCardLike = (input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): boolean => {
  const autocomplete = input.getAttribute('autocomplete') ?? '';
  return autocomplete.startsWith('cc-') || CARD_HINTS.test(input.name) || CARD_HINTS.test(input.id);
};

const propertyOf = (input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string | null => {
  const name = input.name.toLowerCase();
  if ((input instanceof HTMLInputElement && input.type === 'email') || name === 'email' || input.getAttribute('autocomplete') === 'email') {
    return '$email';
  }
  if (name === 'name' || name === 'full_name' || name === 'fullname' || input.getAttribute('autocomplete') === 'name') {
    return '$name';
  }
  return null;
};

/** What a submitted form of the site's own sends: its named answers, never a
 *  password, card-like or hidden value, with email and name mapped to the
 *  person. */
export const collected = (form: HTMLFormElement, pagePath: string): EventProperties | null => {
  if (form.closest('[data-prodantix-ignore]') || form.hasAttribute('data-prodantix-form')) {
    return null;
  }
  const answers: Record<string, string> = {};
  const set: Record<string, string> = {};
  for (const input of Array.from(form.elements)) {
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement || input instanceof HTMLTextAreaElement)) {
      continue;
    }
    if (!input.name || input.closest('[data-prodantix-ignore]') || isCardLike(input)) {
      continue;
    }
    if (input instanceof HTMLInputElement && (SKIPPED_TYPES.has(input.type) || ((input.type === 'checkbox' || input.type === 'radio') && !input.checked))) {
      continue;
    }
    const value = input.value.trim();
    if (value === '') {
      continue;
    }
    answers[input.name] = value;
    const property = propertyOf(input);
    if (property) {
      set[property] = value;
    }
  }
  return {
    $answers: answers,
    $form_id: form.id || `${pagePath}#${Array.from(form.ownerDocument.forms).indexOf(form)}`,
    $page_url: form.ownerDocument.location?.href ?? '',
    $set: set,
    collected: true,
  };
};

/** Captures every submission of the site's own forms. */
export const installFormCollection = (client: Capturer, doc: Document = document): (() => void) => {
  const onSubmit = (event: Event): void => {
    if (event.target instanceof HTMLFormElement) {
      const properties = collected(event.target, doc.location?.pathname ?? '');
      if (properties) {
        client.capture(COLLECTED_FORM_EVENT, { properties });
      }
    }
  };
  doc.addEventListener('submit', onSubmit, true);
  return () => doc.removeEventListener('submit', onSubmit, true);
};
